import LeyendaIlustracion from '@/components/marketing/comun/LeyendaIlustracion';
import MarcoEscalado from '@/components/marketing/comun/MarcoEscalado';
import { CLASE_TONO } from '@/components/marketing/comun/tonosRegistro';
import { CLAVES_BIBLIOTECA, GEOMETRIA_MOVIL, generarRegistros, TRAZADO_MOVIL } from '@/lib/marketing/geometriaBiblioteca';
import { cn } from '@/lib/utils';

import {
    estadoNodo,
    flotacionNodo,
    IconoFuncion,
    REGISTROS_ESCRITORIO,
    SectoresBiblioteca,
    TRANSICION_ICONO,
    TRANSICION_REGISTRO,
    type EstadoNodo,
    type Flotacion,
} from './EscenarioBibliotecaEscritorio';
import { CENTRO_MOVIL, desplazamientoRegistro, SEPARACION_MOVIL, transformacionRegistro } from './estadoBiblioteca';
import { contadorRegistros, etiquetaFuncion, TEXTOS_FUNCIONES, TEXTOS_SECCION } from './textosBiblioteca';

const REGISTROS_MOVIL = generarRegistros(GEOMETRIA_MOVIL);

// Margen alrededor del escenario de 326×340: el halo y la flotación de los iconos del borde
// salen unos px fuera, y el marco escalado recorta lo que se sale de su caja.
const MARGEN = 4;

// Esquina de cada icono de 44 px y sitio de su nombre (a un lado, encima o debajo), del tablero.
const NODOS: readonly ({ izquierda: number; arriba: number; nombre: string } & Flotacion)[] = [
    { izquierda: 1, arriba: 90, nombre: 'left-0 bottom-[50px]', flota: 2, duracion: 1.8, retraso: -0.2 },
    { izquierda: 83, arriba: 8, nombre: 'right-[50px] top-3', flota: 3, duracion: 2.2, retraso: -1.3 },
    { izquierda: 199, arriba: 8, nombre: 'left-[50px] top-3', flota: 1, duracion: 1.6, retraso: -0.7 },
    { izquierda: 281, arriba: 90, nombre: 'right-0 bottom-[50px]', flota: 3, duracion: 2.4, retraso: -1.9 },
    { izquierda: 281, arriba: 206, nombre: 'right-0 top-[50px]', flota: 2, duracion: 1.95, retraso: -0.5 },
    {
        izquierda: 199,
        arriba: 288,
        nombre: 'left-[50px] top-1 w-[76px] whitespace-normal rounded-[10px] px-[7px] py-[2px] leading-[15px]',
        flota: 3,
        duracion: 2.1,
        retraso: -1.6,
    },
    { izquierda: 83, arriba: 288, nombre: 'right-[50px] top-3', flota: 1, duracion: 1.7, retraso: -1 },
    { izquierda: 1, arriba: 206, nombre: 'left-0 top-[50px]', flota: 2, duracion: 2.3, retraso: -2.1 },
];

const CLASE_ICONO: Record<EstadoNodo, string> = {
    reposo: 'bg-surface-2 border-ink text-primary shadow-ds-chip',
    activa: 'bg-accent-blue border-ink text-ink shadow-ds-halo-activo-movil',
    atenuada: 'bg-surface-2 border-ink/25 text-ink/55 shadow-ds-chip-atenuada',
};

interface PropiedadesDibujo {
    activa: number;
    reducido: boolean;
}

function Registros({ activa, reducido }: PropiedadesDibujo) {
    return REGISTROS_MOVIL.map((registro, i) => {
        const esActiva = registro.sector === activa;
        const { mas } = desplazamientoRegistro({ registro, activa: esActiva, separacion: SEPARACION_MOVIL, puntero: null, reducido });
        return (
            <span
                key={i}
                data-registro
                className={cn('pointer-events-none absolute left-0 top-0 box-border rounded-[1.5px] border border-ink', TRANSICION_REGISTRO, CLASE_TONO[registro.tono])}
                style={{
                    width: registro.largo,
                    height: registro.grosor,
                    opacity: activa >= 0 && !esActiva ? 0.3 : 1,
                    transformOrigin: '0 0',
                    transform: transformacionRegistro(registro, CENTRO_MOVIL, mas),
                }}
            />
        );
    });
}

interface PropiedadesNodo extends PropiedadesDibujo {
    indice: number;
    pulsada: boolean;
}

function NodoFuncion({ indice, activa, pulsada, reducido }: PropiedadesNodo) {
    const texto = TEXTOS_FUNCIONES[CLAVES_BIBLIOTECA[indice]];
    const nodo = NODOS[indice];
    const estado = estadoNodo(indice, activa);
    const flotacion = flotacionNodo(nodo, estado === 'activa', reducido);
    return (
        <div
            className={cn('absolute size-11', estado === 'activa' ? 'z-[5]' : 'z-[1]', flotacion.className)}
            style={{ left: nodo.izquierda, top: nodo.arriba, ...flotacion.style }}
        >
            <button
                type="button"
                data-fn={indice}
                data-activa={estado === 'activa' ? '' : undefined}
                aria-pressed={pulsada}
                aria-label={etiquetaFuncion(texto)}
                className={cn(
                    'm-0 box-border flex size-11 cursor-pointer items-center justify-center rounded-full border-2 p-0 focus-visible:outline-offset-4 [-webkit-tap-highlight-color:transparent]',
                    TRANSICION_ICONO,
                    CLASE_ICONO[estado],
                )}
            >
                <IconoFuncion texto={texto} />
            </button>
            <span
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute box-border whitespace-nowrap rounded-full border-[1.5px] border-primary bg-surface-2 px-2 text-[12px] font-bold leading-[18px] text-ink transition-opacity duration-200',
                    nodo.nombre,
                    estado === 'activa' ? 'opacity-100' : 'opacity-0',
                )}
            >
                {texto.nombre}
            </span>
        </div>
    );
}

// El panel de debajo mide siempre 88 px: en reposo invita a tocar y con una función activa
// dice qué hace. Es la región viva que anuncia el cambio a un lector de pantalla.
function Panel({ activa, idBase }: { activa: number; idBase: string }) {
    const texto = activa >= 0 ? TEXTOS_FUNCIONES[CLAVES_BIBLIOTECA[activa]] : null;
    return (
        <div
            id={`${idBase}-panel-movil`}
            aria-live="polite"
            className={cn(
                'box-border flex h-[88px] items-center gap-3 rounded-[14px] border-2 px-3.5 transition-[border-color,background-color,box-shadow] duration-200',
                texto ? 'border-ink bg-tint-accent shadow-ds-chip' : 'border-dashed border-ink/40 bg-surface-suave',
            )}
        >
            <span aria-hidden="true" className={cn('size-2.5 shrink-0 rounded-full', texto ? 'bg-primary' : 'bg-ink/40')} />
            <p className={cn('m-0 text-[15px] leading-[1.45]', texto ? 'text-ink/90' : 'text-on-surface-variant')}>
                {texto ? (
                    <>
                        <strong className="font-bold text-ink">{texto.nombre}</strong> · {texto.descripcion}
                    </>
                ) : (
                    TEXTOS_SECCION.panelReposo
                )}
            </p>
        </div>
    );
}

interface PropiedadesEscenario extends PropiedadesDibujo {
    pulsada: number;
    idBase: string;
}

/**
 * El árbol táctil de la Biblioteca (Biblioteca_Movil.dc.html), por debajo de lg. Sin hover ni
 * escuchas propias: los toques llegan al manejador de clic de la sección, que vale para los
 * dos árboles. El contador habla de la memoria entera, así que dice lo mismo que en escritorio.
 */
export default function EscenarioBibliotecaMovil({ activa, pulsada, idBase, reducido }: PropiedadesEscenario) {
    return (
        <div data-arbol="movil" data-nosnippet className="mx-auto mt-7 w-full max-w-[358px] lg:hidden">
            <div className="box-border flex flex-col gap-3.5 rounded-xl border-2 border-ink bg-surface-2 px-2.5 py-4 shadow-ds-2">
                <div className="flex flex-col items-start gap-2 px-1">
                    <strong className="font-display text-[18px] font-bold leading-[1.2] tracking-[-0.01em]">{TEXTOS_SECCION.tituloTarjeta}</strong>
                    <span className="box-border flex h-[30px] items-center gap-2 whitespace-nowrap rounded-full border-2 border-ink bg-tint-accent px-3 text-[13px] font-bold tabular-nums text-ink">
                        <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-primary" />
                        {contadorRegistros(REGISTROS_ESCRITORIO.length)}
                    </span>
                </div>
                <MarcoEscalado anchoDiseno={326 + 2 * MARGEN} altoDiseno={340 + 2 * MARGEN} escalaInicial={1}>
                    <div id={`${idBase}-escenario-movil`} data-bib className="absolute h-[340px] w-[326px]" style={{ left: MARGEN, top: MARGEN }}>
                        <svg aria-hidden="true" width="326" height="340" className="absolute left-0 top-0 overflow-visible">
                            <SectoresBiblioteca trazado={TRAZADO_MOVIL} activa={activa} reducido={reducido} />
                        </svg>
                        <Registros activa={activa} reducido={reducido} />
                        <span
                            role="img"
                            aria-label={TEXTOS_SECCION.paciente}
                            className="absolute left-[136px] top-[143px] flex size-[54px] items-center justify-center rounded-full bg-ink font-display text-[18px] font-bold tracking-[-0.01em] text-canvas shadow-ds-halo-paciente-movil"
                        >
                            {TEXTOS_SECCION.inicialesPaciente}
                        </span>
                        {CLAVES_BIBLIOTECA.map((clave, i) => (
                            <NodoFuncion key={clave} indice={i} activa={activa} pulsada={pulsada === i} reducido={reducido} />
                        ))}
                    </div>
                </MarcoEscalado>
                <div className="px-1">
                    <Panel activa={activa} idBase={idBase} />
                </div>
                <div className="px-1">
                    <LeyendaIlustracion />
                </div>
            </div>
        </div>
    );
}
